import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Share2, Trash2, Loader2, Mail, Crown, Edit3, Eye, ChevronDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERMS = [
  { value: "view", label: "View", desc: "Read-only", Icon: Eye },
  { value: "edit", label: "Edit", desc: "Add/edit/complete tasks", Icon: Edit3 },
  { value: "manage", label: "Manage", desc: "Edit + invite others, rename list", Icon: Crown },
];

/**
 * ShareListDialog — share a single task list with another registered (or yet-to-be-registered) user.
 * Pro-gated server-side; the dialog surfaces the upgrade nudge if the user is on Free.
 */
export default function ShareListDialog({ list, open, onClose, onPaywall }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [permission, setPermission] = useState("edit");

  const load = useCallback(async () => {
    if (!list) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/lists/${list.id}/shares`);
      setShares(data);
    } catch (e) {
      if (e.response?.status !== 404) toast.error("Couldn't load shares");
    } finally {
      setLoading(false);
    }
  }, [list]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/lists/${list.id}/shares`, {
        email: email.trim().toLowerCase(),
        permission,
      });
      setShares((s) => [...s.filter((x) => x.id !== data.id), data]);
      setEmail("");
      toast.success(data.target_user_id ? `Invited ${data.target_email}` : `Pending invite sent to ${data.target_email}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402 && detail?.code === "upgrade_required") {
        onClose();
        onPaywall?.({ feature: detail.feature || "list sharing", message: detail.message });
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Couldn't send invite");
    } finally {
      setBusy(false);
    }
  };

  const updatePerm = async (shareId, newPerm) => {
    try {
      await axios.patch(`${API}/lists/${list.id}/shares/${shareId}`, { permission: newPerm });
      setShares((s) => s.map((x) => (x.id === shareId ? { ...x, permission: newPerm } : x)));
    } catch {
      toast.error("Couldn't update permission");
    }
  };

  const revoke = async (shareId) => {
    if (!window.confirm("Revoke this share?")) return;
    try {
      await axios.delete(`${API}/lists/${list.id}/shares/${shareId}`);
      setShares((s) => s.filter((x) => x.id !== shareId));
      toast.success("Share revoked");
    } catch {
      toast.error("Couldn't revoke");
    }
  };

  if (!open || !list) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="share-list-dialog">
      <div className="bg-white border border-zinc-200 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <header className="border-b border-zinc-200 px-5 py-4 flex items-center justify-between">
          <h2 className="text-base font-semibold tracking-tight flex items-center gap-2">
            <Share2 className="w-4 h-4 text-[#002FA7]" strokeWidth={1.75} />
            Share "{list.name}"
          </h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900" aria-label="Close" data-testid="share-list-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <form onSubmit={invite} className="px-5 py-4 border-b border-zinc-200 space-y-3" data-testid="share-list-invite-form">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1.5 block">
              Invite by email
            </label>
            <div className="relative">
              <Mail className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="teammate@example.com"
                required
                className="w-full h-11 pl-10 pr-3 text-sm border border-zinc-300 focus:border-[#002FA7] outline-none"
                data-testid="share-list-email-input"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1.5 block">Permission</label>
            <div className="grid grid-cols-3 gap-1.5">
              {PERMS.map(({ value, label, desc, Icon }) => (
                <button
                  type="button"
                  key={value}
                  onClick={() => setPermission(value)}
                  className={`text-left p-2.5 border transition-colors ${permission === value ? "border-[#002FA7] bg-blue-50" : "border-zinc-200 hover:border-zinc-400"}`}
                  data-testid={`share-list-perm-${value}`}
                >
                  <div className="flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5 text-zinc-600" strokeWidth={1.5} />
                    <span className="text-xs font-semibold">{label}</span>
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-0.5 leading-tight">{desc}</p>
                </button>
              ))}
            </div>
          </div>
          <button
            type="submit"
            disabled={busy}
            className="w-full text-xs uppercase tracking-[0.12em] font-medium py-2.5 bg-zinc-900 hover:bg-[#002FA7] text-white transition-colors disabled:opacity-50"
            data-testid="share-list-send-btn"
          >
            {busy ? <span className="inline-flex items-center gap-2 justify-center"><Loader2 className="w-3.5 h-3.5 animate-spin" />Sending…</span> : "Send invite"}
          </button>
          <p className="text-[10px] text-zinc-400 leading-tight">
            If the email isn't registered yet, they'll get access automatically when they sign up.
          </p>
        </form>

        <div className="px-5 py-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2">
            Members ({shares.length})
          </p>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-zinc-500 py-4"><Loader2 className="w-3.5 h-3.5 animate-spin" />Loading…</div>
          ) : shares.length === 0 ? (
            <p className="text-sm text-zinc-500 italic py-2">No one's invited yet.</p>
          ) : (
            <ul className="space-y-2" data-testid="share-list-members">
              {shares.map((s) => (
                <li key={s.id} className="flex items-center gap-3 border border-zinc-200 px-3 py-2.5" data-testid={`share-list-member-${s.id}`}>
                  <div className="w-8 h-8 bg-zinc-100 border border-zinc-300 flex items-center justify-center text-xs font-semibold text-zinc-700 shrink-0">
                    {s.target_email.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-900 truncate">{s.target_email}</p>
                    <p className="text-[10px] text-zinc-500">{s.accepted_at ? "Accepted" : "Pending"}</p>
                  </div>
                  <PermPicker value={s.permission} onChange={(v) => updatePerm(s.id, v)} testid={`share-list-perm-pick-${s.id}`} />
                  <button
                    onClick={() => revoke(s.id)}
                    className="p-1.5 text-zinc-400 hover:text-red-700 hover:bg-red-50 transition-colors"
                    title="Revoke"
                    data-testid={`share-list-revoke-${s.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function PermPicker({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const current = PERMS.find((p) => p.value === value);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 border border-zinc-300 hover:border-zinc-900 transition-colors"
        data-testid={testid}
      >
        {current?.label || value}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-9 z-20 bg-white border border-zinc-200 shadow-lg w-40 py-1">
            {PERMS.map(({ value: v, label, desc }) => (
              <button
                key={v}
                onClick={() => { onChange(v); setOpen(false); }}
                className={`w-full px-3 py-1.5 text-left text-xs hover:bg-zinc-50 ${v === value ? "bg-blue-50 text-[#002FA7]" : "text-zinc-700"}`}
                data-testid={`${testid}-${v}`}
              >
                <div className="font-semibold">{label}</div>
                <div className="text-[10px] text-zinc-500">{desc}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
